import isNil from "lodash/isNil";

import { WeightTrackingTestID } from "@/constants/TestID";
import BoldWeightText from "@/ui/components/vitals/BoldWeightText";
import { Icon, Text } from "@/ui/core";
import { HStack, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";
import {
  getRoundedValue,
  getWeightChangeIconProps,
  isWeightChangeSignificant,
} from "@/utils/vitals/weight.util";

interface RecentWeightSummaryProps {
  isWeeklyWeightChange: boolean;
  loggedBy: string;
  unit: string;
  value: number;
  weightChange?: number;
}

const RecentWeightSummary = ({
  isWeeklyWeightChange,
  loggedBy,
  unit,
  value,
  weightChange,
}: RecentWeightSummaryProps) => {
  const baseTestID = WeightTrackingTestID.Overview;
  const [iconColor, iconName] = getWeightChangeIconProps(weightChange);
  const Illustration = (
    <Icon color={iconColor} name={iconName} size="sm" testID={baseTestID} />
  );
  const isChangeSignificant =
    weightChange && isWeightChangeSignificant(weightChange);
  const weightChangeMessage = isWeeklyWeightChange
    ? `${unit} in past 7 days`
    : `${unit} difference in the last two weights`;

  const noChangeMessage = isNil(weightChange)
    ? "No weight logged in the last 7 days"
    : "No weight change in the last 7 days";

  return (
    <VStack gap={12}>
      <VStack gap={2}>
        <HStack alignItems="flex-end" gap={4}>
          <Text
            testID={joinTestIDs(baseTestID, WeightTrackingTestID.WeightValue)}
            variant="h4"
          >
            {getRoundedValue(value)}
          </Text>
          <Text
            testID={joinTestIDs(baseTestID, WeightTrackingTestID.WeightUnit)}
            variant="h5"
          >
            {unit}
          </Text>
        </HStack>
        <Text
          color={ColorToken.NeutralSecondary}
          testID={baseTestID}
          variant="caption"
        >
          {loggedBy}
        </Text>
      </VStack>
      <HStack alignItems="center" gap={4} justifyContent="flex-start">
        {Illustration}
        <HStack alignItems="flex-end">
          {isChangeSignificant ? (
            <BoldWeightText
              testID={baseTestID}
              text={weightChangeMessage}
              weight={`${weightChange > 0 ? "+" : "-"}${Math.abs(getRoundedValue(weightChange))}`}
            />
          ) : (
            <Text
              testID={joinTestIDs(baseTestID, WeightTrackingTestID.NoChange)}
              variant="bodyMedium"
            >
              {noChangeMessage}
            </Text>
          )}
        </HStack>
      </HStack>
    </VStack>
  );
};

export type { RecentWeightSummaryProps };
export default RecentWeightSummary;
